<template>
  <!-- Round-3 shared: fullscreen PDF viewer (reused by #164 PO, #165 invoice, docs).
       Supply EITHER :pdf-url (direct/presigned URL) OR :fetch-pdf (fn → Promise<blob|url>). -->
  <el-dialog
    :title="title || $t('mt_shared.pdf_title')"
    :visible.sync="innerVisible"
    fullscreen
    :close-on-click-modal="false"
    custom-class="mt-pdf-fullscreen-dialog"
    append-to-body
    @opened="onOpened"
    @closed="onClosed"
  >
    <div class="mt-pdf-body" v-loading="loading" :element-loading-text="$t('mt_shared.loading')">
      <iframe
        v-if="displayUrl"
        :src="displayUrl"
        class="mt-pdf-frame"
        frameborder="0"
      ></iframe>
      <div v-else-if="errorMsg" class="mt-pdf-empty text-danger">
        <i class="fa fa-exclamation-triangle mr-1"></i>{{ errorMsg }}
      </div>
      <div v-else-if="!loading" class="mt-pdf-empty text-muted">
        {{ $t('mt_shared.no_pdf') }}
      </div>
    </div>

    <span slot="footer">
      <el-button
        type="primary"
        icon="el-icon-download"
        :disabled="!downloadUrl"
        @click="download"
      >{{ $t('mt_shared.download') }}</el-button>
      <el-button @click="innerVisible = false">{{ $t('mt_shared.close') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { Dialog, Button, Loading } from "element-ui";

export default {
  name: "PdfFullscreenModal",
  components: {
    [Dialog.name]: Dialog,
    [Button.name]: Button,
  },
  directives: {
    loading: Loading.directive,
  },
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: "" },
    // Direct or presigned URL to a PDF.
    pdfUrl: { type: String, default: "" },
    // Optional loader: () => Promise resolving to a Blob, an axios response
    // ({ data: Blob }), or a URL string. Preferred for authed blob fetches.
    fetchPdf: { type: Function, default: null },
    // Suggested filename for the download button.
    filename: { type: String, default: "document.pdf" },
  },
  data() {
    return {
      innerVisible: this.visible,
      loading: false,
      blobUrl: "",   // object URL we created (must be revoked)
      directUrl: "", // pdfUrl passed through
      errorMsg: "",
    };
  },
  computed: {
    // iframe source (blob url wins when we fetched one)
    displayUrl() {
      return this.blobUrl || this.directUrl || "";
    },
    // what the download button targets
    downloadUrl() {
      return this.blobUrl || this.directUrl || "";
    },
  },
  watch: {
    visible(v) {
      this.innerVisible = v;
    },
    innerVisible(v) {
      this.$emit("update:visible", v);
    },
  },
  methods: {
    async onOpened() {
      this.errorMsg = "";
      if (this.fetchPdf) {
        await this.loadViaFetch();
      } else if (this.pdfUrl) {
        this.directUrl = this.pdfUrl;
      }
    },
    async loadViaFetch() {
      this.loading = true;
      try {
        const res = await this.fetchPdf();
        let out = res;
        // axios response → unwrap .data
        if (res && res.data !== undefined) out = res.data;
        if (typeof out === "string") {
          // resolved to a URL directly
          this.directUrl = out;
        } else {
          const blob = out instanceof Blob ? out : new Blob([out], { type: "application/pdf" });
          this.revokeBlob();
          this.blobUrl = window.URL.createObjectURL(blob);
        }
      } catch (e) {
        this.errorMsg =
          (e && e.response && e.response.data && e.response.data.Cause) ||
          (e && e.message) ||
          this.$t("mt_shared.pdf_error");
      } finally {
        this.loading = false;
      }
    },
    download() {
      if (!this.downloadUrl) return;
      const a = document.createElement("a");
      a.href = this.downloadUrl;
      a.download = this.filename || "document.pdf";
      // For cross-origin presigned URLs the browser may open instead of save;
      // that is acceptable — the user still gets the file.
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    onClosed() {
      this.revokeBlob();
      this.directUrl = "";
      this.errorMsg = "";
    },
    revokeBlob() {
      if (this.blobUrl) {
        window.URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = "";
      }
    },
  },
  beforeDestroy() {
    this.revokeBlob();
  },
};
</script>

<style>
.mt-pdf-fullscreen-dialog .el-dialog__body {
  padding: 0;
  /* fullscreen dialog header ~54px + footer ~62px */
  height: calc(100vh - 116px);
}
.mt-pdf-body {
  width: 100%;
  height: 100%;
}
.mt-pdf-frame {
  width: 100%;
  height: 100%;
  display: block;
}
.mt-pdf-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 1rem;
}
</style>
